import { Loader } from '@pwc/pwc-deals-ui';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useLocation, useParams } from 'react-router-dom';
import qs from 'query-string';
import { Constants, NavigationNodeId } from '@pwc/pwc-deals-web-common';
import { getNavigationTitle } from 'common/helpers/getCustomNavigationTitles';
import styles from './ReportPreview.module.scss';
import { ENTITY_TYPES } from '../../constants/General';
import {
  getCurrentAnalysis,
  resetAnalysis,
} from '../../../features/analyses/analysisSlice';
import {
  loadAnalysisThunk,
  loadStructuredAnalysisThunk,
} from '../../../features/analyses/analysisSliceThunk';
import { getReportPreview } from './ReportService';
import StatusViewer from '../StatusViewer/StatusViewer';
import {
  viewAnalysisPath,
  viewStructuredAnalysisPathForSubType,
  viewHypothesisPath,
} from '../../services/urlService';
import PreviewLoadingError from '../PreviewLoadingError';
import { TopBar } from './TopBar';

const ReportPreview = () => {
  const KFATitle = getNavigationTitle(NavigationNodeId.KEY_FOCUS_AREAS);

  const { projectId, entityId, version, entityType } = useParams();
  const location = useLocation();
  const [reportData, setReportData] = useState([]);
  const [isReportLoading, setIsReportLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const dispatch = useDispatch();
  const analysis = useSelector(getCurrentAnalysis);

  const { isUnStructured } = qs.parse(location.search);

  useEffect(() => {
    async function fetchReportPreview() {
      try {
        const reportPreview = await getReportPreview(
          projectId,
          entityId,
          version
        );
        setReportData(reportPreview);
      } catch (e) {
        setHasError(true);
      } finally {
        setIsReportLoading(false);
      }
    }

    fetchReportPreview();
  }, [entityId, projectId, version]);

  useEffect(() => {
    const analysisVersion =
      version === 'shared' ? Constants.VERSION_STATUS.SHARED.value : '';
    if (isUnStructured === 'false') {
      dispatch(
        loadStructuredAnalysisThunk(projectId, entityId, analysisVersion)
      );
    } else {
      dispatch(loadAnalysisThunk(projectId, entityId, analysisVersion));
    }
    return () => dispatch(resetAnalysis());
  }, [dispatch, projectId, entityId, version, isUnStructured]);

  const getBackLink = () => {
    if (entityType === ENTITY_TYPES.ANALYSIS) {
      if (analysis.isUnStructured) {
        return viewAnalysisPath(projectId, analysis.contentThreadId);
      }
      return viewStructuredAnalysisPathForSubType(
        projectId,
        analysis.contentSubType
      );
    }
    return viewHypothesisPath(projectId, entityId);
  };
  const analysisLink = getBackLink();

  return (
    <div className={styles.reportPreview}>
      <TopBar
        backLink={analysisLink}
        backLabel={entityType === ENTITY_TYPES.KFA ? KFATitle : 'analysis'}
        entityId={entityId}
      />

      <div className={styles['analysis-report-preview-container']}>
        <div className={styles.reportPreview__inner}>
          <div className={`${styles.reportPreview__inner__status}`}>
            {analysis.contentId && (
              <StatusViewer
                projectId={projectId}
                content={analysis}
                className="analysis-pill-container"
                allowDraftPill
                shareWithAll={false}
              />
            )}
          </div>
        </div>
        {hasError && <PreviewLoadingError />}
        <>
          {isReportLoading || analysis.isLoading ? (
            <Loader dataTestId="loading-spinner" />
          ) : (
            reportData.map((image) => {
              return (
                <img
                  className={styles['preview-image']}
                  key={image}
                  src={image}
                  alt="report preview"
                />
              );
            })
          )}
        </>
      </div>
    </div>
  );
};

export default ReportPreview;
